'use client'

import {
  Bot,
  Shield,
  FileText,
  Zap,
  Quote,
  MessageCircle,
  ArrowRight,
  ScanSearch,
  SearchCheck,
  Waypoints,
  BarChart3,
  ShieldCheck,
  BrainCircuit,
} from 'lucide-react'
import { RadarMark } from '@/components/brand/RadarMark'

const TOOLS = [
  { name: 'Crawl Check', icon: Bot, color: '#008dcc' },
  { name: 'Robots.txt', icon: Shield, color: '#8b5cf6' },
  { name: 'llms.txt', icon: FileText, color: '#22c55e' },
  { name: 'AI Readiness', icon: Zap, color: '#FDC304' },
  { name: 'Citations', icon: Quote, color: '#F90B8A' },
  { name: 'Reddit', icon: MessageCircle, color: '#f97316' },
  { name: 'AEO Audit', icon: ScanSearch, color: '#7c3aed' },
  { name: 'Citation Test', icon: SearchCheck, color: '#dc2626' },
  { name: 'Source Map', icon: Waypoints, color: '#06b6d4' },
  { name: 'Prompt SOV', icon: BarChart3, color: '#a855f7' },
  { name: 'Schema Audit', icon: ShieldCheck, color: '#14b8a6' },
  { name: 'Hallucination', icon: BrainCircuit, color: '#ef4444' },
]

interface PlatformCTAProps {
  currentTool: string // name of the tool the user just used
  score?: number
  grade?: string
}

/**
 * CTA shown after tool results, inviting users to run the full
 * Radar audit with all 13 tools.
 * Opens /platform in a new tab.
 */
export default function PlatformCTA({
  currentTool,
  score,
  grade,
}: PlatformCTAProps) {
  const otherTools = TOOLS.filter(
    t => t.name.toLowerCase() !== currentTool.toLowerCase()
  )

  return (
    <div
      className='mt-8 rounded-2xl border border-border/60 overflow-hidden'
      style={{
        background: 'linear-gradient(135deg, #0d132108, #008dcc05, #008dcc05)',
      }}
    >
      <div className='p-6 md:p-8'>
        <div className='flex flex-col md:flex-row items-start md:items-center gap-6'>
          {/* Left: message */}
          <div className='flex-1'>
            <div className='flex items-center gap-2 mb-2'>
              <RadarMark className='h-4 w-4 shrink-0 text-foreground' />
              <span
                className='text-xs font-bold uppercase tracking-wider'
                style={{ color: '#FDC304' }}
              >
                Radar by Pixelmojo
              </span>
            </div>
            <h3 className='text-lg font-bold text-foreground mb-2'>
              {score !== undefined
                ? `You scored ${score}/100${grade ? ` (${grade})` : ''}. What about the rest?`
                : 'See your complete AI visibility picture'}
            </h3>
            <p className='text-sm text-foreground/70 mb-4'>
              Run all 13 tools in staged batches on one domain. Get a unified AI
              visibility score, cross-tool insights that reveal conflicts you
              cannot see from one tool alone, and prioritized action items for
              your LLM/SEO/GEO strategy.
            </p>

            {/* Other tools preview */}
            <div className='flex flex-wrap gap-2 mb-4'>
              {otherTools.map(t => {
                const Icon = t.icon
                return (
                  <span
                    key={t.name}
                    className='flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg bg-muted/50 border border-border/40'
                  >
                    <Icon size={11} style={{ color: t.color }} />
                    <span className='text-foreground/70'>{t.name}</span>
                  </span>
                )
              })}
            </div>

            <a
              href='/platform'
              data-cta-id='tool_result_full_scan'
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-cta-fill hover:bg-cta-fill-hover transition-colors'
            >
              Run Radar Scan
              <ArrowRight size={14} />
            </a>
          </div>

          {/* Right: visual */}
          <div className='hidden md:block flex-shrink-0'>
            <div className='relative w-28 h-28'>
              <svg className='w-full h-full -rotate-90' viewBox='0 0 100 100'>
                <circle
                  cx='50'
                  cy='50'
                  r='42'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='4'
                  className='text-muted/50'
                />
                {TOOLS.map((t, i) => {
                  const angle = (i / TOOLS.length) * Math.PI * 2 - Math.PI / 2
                  const x = 50 + 42 * Math.cos(angle)
                  const y = 50 + 42 * Math.sin(angle)
                  return (
                    <circle key={t.name} cx={x} cy={y} r='4' fill={t.color} />
                  )
                })}
              </svg>
              <div className='absolute inset-0 flex flex-col items-center justify-center'>
                <span className='text-xs font-bold text-foreground/70'>
                  13 tools
                </span>
                <span className='text-[9px] text-foreground/70'>1 report</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
